import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { cleanAmount, cleanPurpose } from "../../shared/embed";
import { isSlugParam } from "../../shared/routeParams";
import { useTurnstile } from "../lib/turnstile";
import { ArrowIcon, Logo, PayerSteps } from "../lib/ui";

interface OrgInfo {
  orgName: string;
  /** The org's current link — differs from the URL when the payer arrived on
   *  one the org has since retired. */
  slug: string;
  /** False while the org has switched its public link off. */
  enabled: boolean;
  /** Present only when the bot check is configured on the server. */
  turnstileSiteKey?: string;
}

interface CreatedRequest {
  id: string;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div>
      <header className="col" style={{ display: "flex", alignItems: "center", height: 76 }}>
        <Logo height={24} to={null} />
      </header>
      <main className="col" style={{ paddingTop: 8, paddingBottom: 48 }}>
        {children}
      </main>
    </div>
  );
}

/**
 * An org's public link (confirmpay.link/<slug>): unlike the dashboard's create form, the PAYER
 * fills this in (amount, what it's for, their name) and goes straight into the /pay/:id flow.
 * No session; the org is resolved from the slug.
 */
export default function PayerCreate() {
  const { orgSlug = "" } = useParams();
  // The address is the attacker's to write, and `orgSlug` is put into API
  // paths: anything that isn't shaped like a link is treated as a dead one.
  const validSlug = isSlugParam(orgSlug);
  const nav = useNavigate();
  const [search] = useSearchParams();
  // An embedded button (or an org's own link) can fill in the amount and what
  // it's for. They only pre-fill the form: the payer still sees and can change
  // them, and the server validates whatever is finally submitted.
  const fromButton = search.get("from") === "button";

  const [org, setOrg] = useState<OrgInfo | null>(null);
  const [loadError, setLoadError] = useState("");

  const [amount, setAmount] = useState(() => cleanAmount(search.get("amount")) ?? "");
  const [description, setDescription] = useState(() => cleanPurpose(search.get("for")));
  const [payerName, setPayerName] = useState("");
  // Honeypot: stays empty for real people (it's invisible), bots fill it.
  const [hpNote, setHpNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  // The container is mounted whenever there's a key, in the same render the key first appears,
  // so the widget has an element to attach to when the hook's effect runs.
  const siteKey = org?.enabled ? org.turnstileSiteKey : undefined;
  const turnstile = useTurnstile(siteKey);

  useEffect(() => {
    if (!validSlug) {
      setLoadError("This payment link doesn't exist. Check the link and try again.");
      return;
    }
    api.get<OrgInfo>(`/api/orgs/by-slug/${encodeURIComponent(orgSlug)}`).then((res) => {
      if (!res.ok) {
        setLoadError(
          res.status === 404
            ? "This payment link doesn't exist. Check the link and try again."
            : res.error,
        );
        return;
      }
      // Old link -> move to the live one, so what's in the address bar (and
      // gets shared or bookmarked next) is the current address.
      if (res.data.slug !== orgSlug.toLowerCase()) {
        nav({ pathname: `/${res.data.slug}`, search: window.location.search }, { replace: true });
        return;
      }
      setOrg(res.data);
    });
  }, [orgSlug, validSlug, nav]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!validSlug) return;
    setCreating(true);
    setCreateError("");
    const res = await api.post<CreatedRequest>(`/api/orgs/by-slug/${encodeURIComponent(orgSlug)}/requests`, {
      amount,
      description,
      payerName,
      hpNote,
      turnstileToken: turnstile.token,
    });
    setCreating(false);
    if (!res.ok) {
      setCreateError(res.error);
      turnstile.reset(); // a token is good for one attempt
      return;
    }
    nav(`/pay/${res.data.id}${fromButton ? "?from=button" : ""}`);
  }

  if (loadError) {
    return (
      <Shell>
        <h1 className="h1" style={{ fontSize: 40 }}>Payment link</h1>
        <div className="error-box" role="alert" style={{ marginTop: 18 }}>{loadError}</div>
        <p style={{ margin: "28px 0 0", fontSize: 14, color: "var(--ink3)" }}>
          <Link to="/" style={{ fontWeight: 600, color: "var(--ink)" }}>ConfirmPay</Link>
        </p>
      </Shell>
    );
  }
  if (!org) {
    return (
      <Shell>
        <div className="note note--wait" role="status" style={{ marginTop: 0 }}>
          <span className="spin" /> Loading…
        </div>
      </Shell>
    );
  }
  if (!org.enabled) {
    return (
      <Shell>
        <h1 className="h1" style={{ fontSize: 40 }}>{org.orgName}</h1>
        <div className="card" style={{ marginTop: 22 }}>
          <p style={{ fontSize: 17 }}>
            <strong>{org.orgName}</strong> isn&rsquo;t taking payments through this link right now.
          </p>
          <p className="hint" style={{ marginTop: 10 }}>
            If you were sent here to pay them, please check with them directly for how to pay.
          </p>
        </div>
      </Shell>
    );
  }

  const verifying = turnstile.state === "working";
  const verifyFailed = turnstile.state === "failed";

  return (
    <Shell>
      <PayerSteps active={1} />

      <h1 className="h1" style={{ fontSize: 42, letterSpacing: "-0.045em" }}>Pay {org.orgName}</h1>
      <p style={{ margin: "14px 0 30px", fontSize: 17, color: "var(--ink2)" }}>
        You pay them directly by PayNow. ConfirmPay never touches the money.
      </p>

      <form onSubmit={submit}>
        <div className="field">
          <label className="lbl" htmlFor="p-amt" style={{ fontSize: 15 }}>Amount</label>
          <div className="prefix-wrap">
            <span className="prefix" style={{ fontSize: 22, left: 16 }}>S$</span>
            <input
              id="p-amt"
              className="inp inp--mono inp--big"
              type="number"
              inputMode="decimal"
              min="0.01"
              step="0.01"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="20.00"
            />
          </div>
        </div>
        <div className="field">
          <label className="lbl" htmlFor="p-for" style={{ fontSize: 15 }}>
            What is it for? <span className="opt">(optional)</span>
          </label>
          <input
            id="p-for"
            className="inp"
            style={{ height: 54, fontSize: 17 }}
            maxLength={140}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Ramadan food drive"
          />
        </div>
        <div className="field" style={{ marginBottom: 26 }}>
          <label className="lbl" htmlFor="p-name" style={{ fontSize: 15 }}>Your name</label>
          <input
            id="p-name"
            className="inp"
            style={{ height: 54, fontSize: 17 }}
            maxLength={80}
            required
            autoComplete="name"
            value={payerName}
            onChange={(e) => setPayerName(e.target.value)}
            placeholder="e.g. Hafiz"
          />
          <p className="hint" style={{ fontSize: 14 }}>
            Helps {org.orgName} match your payment if their bank&rsquo;s notification has no reference.
          </p>
        </div>

        <div className="hp-field" aria-hidden="true">
          <label>
            Leave this empty
            <input type="text" name="hp_note" tabIndex={-1} autoComplete="off" value={hpNote} onChange={(e) => setHpNote(e.target.value)} />
          </label>
        </div>

        {siteKey && <div ref={turnstile.containerRef} />}

        <button
          className="btn btn--block"
          style={{ height: 56, fontSize: 17 }}
          disabled={creating || !payerName.trim() || !amount.trim() || (Boolean(siteKey) && !turnstile.token)}
        >
          {creating ? "Creating…" : "Continue to payment"}
          {!creating && <ArrowIcon size={20} />}
        </button>
        <p className="lg-consent">
          We use your name to match your payment and send your receipt. See the <Link to="/privacy">Privacy Policy</Link>.
        </p>
        {verifying && !creating && (
          <p className="hint" role="status">Checking that you&rsquo;re a person…</p>
        )}
        {verifyFailed && (
          <div className="error-box" role="alert">
            We couldn&rsquo;t run the check that keeps bots out. If you use a content blocker, pause it for this page and reload.
          </div>
        )}
        {createError && <div className="error-box" role="alert">{createError}</div>}
      </form>

      <section style={{ marginTop: 36, borderTop: "1px solid var(--line2)", paddingTop: 24 }}>
        <h2 style={{ margin: "0 0 14px", fontSize: 17, fontWeight: 700, letterSpacing: "-0.01em" }}>What happens next</h2>
        <ol style={{ listStyle: "none", padding: 0, display: "grid", gap: 14, fontSize: 15.5, color: "var(--ink2)" }}>
          {[
            "You see a PayNow QR for exactly this amount.",
            "Open your banking app, scan or import the QR, and pay.",
            "Come back here after paying. The page confirms it, and your receipt arrives by email.",
          ].map((t, i) => (
            <li key={t} style={{ display: "grid", gridTemplateColumns: "26px minmax(0,1fr)", gap: 12 }}>
              <span className="mono" style={{ fontWeight: 600, color: "var(--ink3)" }}>{i + 1}</span>
              <span>{t}</span>
            </li>
          ))}
        </ol>
      </section>

      <p style={{ margin: "36px 0 0", fontSize: 13.5, color: "var(--ink3)", textAlign: "center" }}>
        Powered by <Link to="/" style={{ fontWeight: 600, color: "var(--ink)" }}>ConfirmPay</Link>, free for Singapore nonprofits.
      </p>
    </Shell>
  );
}
